/**
 * F-010 — Score overlay (top-right).
 *
 * Mounts <div data-testid="score-overlay"> over the canvas, subscribes
 * to the Score instance and re-renders on every update.
 *
 * F-075: the overlay also doubles as the leaderboard entry-point.
 * Click / Enter / Space opens the global rankings page. A 🏆 marker is
 * rendered in the corner so the affordance reads at a glance. The host
 * wires `onClick` from main.ts (separation of concerns — the overlay
 * doesn't know how to open a leaderboard, just that the user wanted to).
 */

import type { Score } from "../game/score.ts";
import { formatNumber, t } from "../i18n/index.ts";

export interface ScoreOverlay {
  element: HTMLDivElement;
  detach(): void;
}

export function attachScoreOverlay(score: Score, host: HTMLElement): ScoreOverlay {
  const element = document.createElement("button");
  element.type = "button";
  element.dataset["testid"] = "score-overlay";
  element.setAttribute("aria-label", t("leaderboard.openButton"));
  // Mobile-first sizing — tighter padding/text on narrow screens, scales
  // up at sm: breakpoint (640px+). Keeps the corner widget unobtrusive.
  // pointer-events-auto so click hits land. cursor-pointer + hover ring
  // hint that this surface is interactive (F-075).
  element.className =
    "pointer-events-auto absolute right-0 top-0 m-3 flex flex-col items-end gap-0 rounded-md bg-black/40 px-3 py-1.5 text-right font-display text-xs text-cosmic-star backdrop-blur-sm transition hover:bg-black/60 hover:ring-1 hover:ring-cosmic-glow/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-cosmic-glow sm:m-6 sm:px-4 sm:py-2 sm:text-sm";

  // 🏆 marker — small enough not to compete with the score numbers but
  // tall enough to telegraph "this opens the leaderboard".
  const trophyRow = document.createElement("div");
  trophyRow.dataset["testid"] = "score-overlay-trophy";
  trophyRow.className =
    "flex items-center gap-1 text-[9px] uppercase tracking-widest text-cosmic-glow/70 sm:text-[10px]";
  trophyRow.textContent = `🏆 ${t("leaderboard.openButton")}`;

  const current = document.createElement("div");
  current.dataset["testid"] = "score-current";
  current.className = "text-base sm:text-2xl";

  const high = document.createElement("div");
  high.dataset["testid"] = "score-high";
  high.className = "text-[10px] text-slate-300/70 sm:text-xs";

  element.append(trophyRow, current, high);
  host.appendChild(element);

  const unsub = score.subscribe((snap) => {
    current.textContent = `${t("score.label")} ${formatNumber(snap.current)}`;
    high.textContent = `${t("score.high")} ${formatNumber(snap.highScore)}`;
  });

  const detach = (): void => {
    unsub();
    element.remove();
  };

  return { element: element as unknown as HTMLDivElement, detach };
}
